import React from 'react';
import { useRouter } from 'next/router';
import { AllSettings, LocaleCode, Blog, FaqQuestion } from '@web-creditea/interfaces';
import { getAlternates, getCanonicalUrl, returnMaybe } from '../../../helpers';
import { NextSeo, SocialProfileJsonLd, FAQPageJsonLd } from 'next-seo';
import { socialMedia } from '../../../data/socialMedia';
import {
  SeoGroupFragment,
  CtaButtonDataFragment,
  LinkRequiredDataFragment,
} from 'libs/interfaces/src/lib/gql/graphql';

type SimpleLayoutProps = {
  children: React.ReactNode;
  showProvidentLogo: boolean;
  title: string;
  seo?: SeoGroupFragment;
  ctaButton?: CtaButtonDataFragment;
  loginButton?: LinkRequiredDataFragment;
  articleInfo?: {
    _createdAt: Blog['_createdAt'];
    publishedDate: Blog['publishedDate'];
  };
  mode?: 'dark' | 'light';
  settings: AllSettings;
};

const SimpleLayout = ({
  articleInfo,
  children,
  showProvidentLogo,
  title,
  seo,
  settings,
}: SimpleLayoutProps) => {
  const router = useRouter();
  const seoTitle = `${seo?.metaTitle ?? title} | Creditea`;
  const basePath =
    router.locale !== router.defaultLocale
      ? `${router.basePath}/${router.locale}`
      : router.basePath;

  const { market } = settings;

  const ogImage = seo?.openGraph?.image?.asset;
  const seoImages = ogImage?.url
    ? [
        {
          url: ogImage?.url ?? '',
          alt: seo?.openGraph?.imageAlt ?? '',
          type: ogImage?.mimeType ?? '',
          height: returnMaybe(ogImage?.metadata?.dimensions?.height),
          width: returnMaybe(ogImage?.metadata?.dimensions?.width),
        },
      ]
    : [];
  const articleMetatdata = articleInfo && {
    publishedTime: String(articleInfo?.publishedDate),
  };
  const blogArticle = router.route === '/blog/[slug]';

  const canonicalURL = getCanonicalUrl(market, basePath, router.asPath);

  const baseLocale = router.basePath.split('/')[1] as keyof typeof socialMedia extends LocaleCode
    ? LocaleCode
    : unknown;
  const socialLinks = socialMedia[baseLocale];

  const prepareFaqSchema = (questions: Array<FaqQuestion | null>) => {
    questions.forEach((question) => {
      delete question?.__typename;
    });
    return questions;
  };

  return (
    <>
      <NextSeo
        noindex
        nofollow
        defaultTitle="Creaditea.com"
        title={seoTitle}
        canonical={seo?.overWriteCanonical ? returnMaybe(seo?.canonicalURL) : canonicalURL.href}
        openGraph={{
          title: seo?.openGraph?.title || seoTitle,
          locale: router.locale,
          type: blogArticle ? 'article' : 'website',
          siteName: 'Creditea.com',
          images: seoImages,
          article: articleMetatdata,
        }}
        languageAlternates={getAlternates(returnMaybe(seo?.languageAlternates))}
        additionalMetaTags={[
          { name: 'commit-build-id', content: process.env['NEXT_PUBLIC_GIT_REV'] || 'localhost' },
          { name: 'facebook-domain-verification', content: '9pzun0zbw7gxbbm1zz227x1u6v5e0w' },
          { name: 'twitter:card', content: seo?.openGraph?.twitterCardType || 'summary' },
          { name: 'twitter:app:name:iphone', content: 'Creditea | Card and Credit Line' },
          { name: 'twitter:app:id:iphone', content: '1546903528' },
          {
            name: 'twitter:app:url:iphone',
            content: 'https://apps.apple.com/ee/app/creditea-card-and-creditline/id1546903528',
          },
          { name: 'twitter:app:name:googleplay', content: 'Creditea | Card and App' },
          { name: 'twitter:app:id:googleplay', content: 'com.ipfdigital.creditea.mw' },
          {
            name: 'twitter:app:url:googleplay',
            content: 'https://play.google.com/store/apps/details?id=com.ipfdigital.creditea.mw',
          },
          { name: 'twitter:app:country', content: baseLocale },
          {
            name: 'twitter:text:title',
            content: 'Creditea | App',
          },
        ]}
      />
      {seo?.faqSchema?.questions?.length && (
        <FAQPageJsonLd mainEntity={prepareFaqSchema(seo.faqSchema.questions)} />
      )}
      {socialLinks?.length > 0 && (
        <SocialProfileJsonLd
          type="Organization"
          name="Creditea"
          url="https://www.creditea.com/"
          sameAs={socialLinks}
        />
      )}
      {children}
      {showProvidentLogo && (
        <svg
          style={{
            position: 'absolute',
            bottom: '2vh',
            right: '5%',
          }}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 628.228 187.51"
          width={140}
        >
          <path
            d="M203.743 52.697c-6.716-5.855-18.406-19.752-80.003 5.46 34.182 9.693 57.588 46.696 33.068 69.014 0 0-.054.054-.136.132 34.741 10.433 50.994 10.505 58.33-18.664 7.446-29.616-4.504-50.054-11.259-55.942Z"
            fill="#8cd900"
          />
          <path
            d="M94.977 82.14c-2.078-10.391 27.711-23.555 27.711-23.555.355-.146.7-.284 1.052-.429-6.138-1.74-12.62-2.605-19.237-2.341-43.472 1.731-62.405 33.55-60.965 51.784 1.23 15.567 12.47 34.294 53.864 36.199 39.433 1.815 57.633-14.905 59.27-16.495-.532-.16-1.055-.314-1.597-.479-35.85-10.91-58.02-34.292-60.098-44.684Z"
            fill="#08b5f7"
          />
          <path
            d="M156.808 127.171c24.52-22.318 1.114-59.321-33.068-69.015-.352.145-.697.283-1.052.43 0 0-29.79 13.163-27.711 23.554 2.078 10.392 24.247 33.773 60.099 44.684.54.165 1.064.32 1.596.48l.136-.133Z"
            fill="#008c3e"
          />
          <path d="M287.095 84.234c0 12.166-8.64 20.012-23.627 20.012-2.997 0-5.554-.088-7.934-.265v19.483a23.716 23.716 0 0 1-5.025.53c-1.675 0-3.262-.177-5.025-.53V67.572c4.672-1.146 10.756-1.94 17.632-1.94 16.75 0 23.98 7.494 23.98 18.602Zm-10.403.088c0-6.083-4.32-9.962-13.312-9.962-2.645 0-5.29.177-7.846.441v20.541c2.292.176 4.937.265 7.934.265 9.521 0 13.224-4.761 13.224-11.285ZM316.632 81.854c0 3.174-.881 6.347-2.644 8.64a25.056 25.056 0 0 0-6.348-.794c-3.262 0-5.994.617-7.845 1.675v32.09c-1.588.352-3.35.44-5.026.44s-3.349-.088-5.025-.44V86.525c3.174-2.82 9.61-5.994 17.632-5.994 3.614 0 6.612.352 9.256 1.322ZM314.737 102.66c0-12.96 9.433-22.217 22.304-22.217s21.95 9.169 21.95 22.216c0 12.871-9.431 22.128-22.302 22.128-12.872 0-21.952-9.08-21.952-22.128Zm34.206 0c0-8.023-4.585-13.577-12.078-13.577-7.494 0-12.078 5.554-12.078 13.576 0 7.934 4.584 13.4 12.078 13.4 7.493 0 12.078-5.466 12.078-13.4ZM400.787 82.383c-3.791 15.516-9.522 29.533-16.221 41.081-1.587.353-3.262.441-4.76.441-1.587 0-3.263-.088-4.938-.44-7.669-12.784-12.43-25.743-16.397-41.082 1.41-.44 4.32-.882 6.877-.882 1.322 0 2.732.177 3.878.353 3.174 12.606 7.317 24.155 10.314 31.031.178.441.354.882.441 1.323l.53-1.323c3.438-7.934 6.965-18.425 10.05-31.031 1.146-.176 2.557-.353 3.879-.353 2.38 0 4.76.353 6.347.882ZM404.15 69.07c0-3.525 2.91-6.346 6.523-6.346 3.439 0 6.347 2.82 6.347 6.347s-2.908 6.347-6.347 6.347c-3.614 0-6.523-2.82-6.523-6.347Zm1.499 54.394V81.766c1.674-.353 3.349-.441 5.024-.441 1.588 0 3.35.088 4.937.44v41.7c-1.587.352-3.262.44-4.937.44-1.586 0-3.35-.088-5.024-.44ZM461.794 65.545v55.098c-4.319 2.292-11.284 4.144-18.072 4.144-13.753 0-23.098-7.758-23.098-21.864 0-13.752 10.227-22.303 22.746-22.303 3.525 0 6.171.44 8.463 1.322V65.545c1.586-.353 3.262-.441 4.936-.441 1.587 0 3.35.088 5.025.44Zm-9.961 49.192v-23.45c-2.38-1.499-5.113-1.852-8.111-1.852-7.405 0-12.96 5.202-12.96 13.312 0 8.199 5.202 13.312 12.784 13.312 2.908.088 5.642-.352 8.287-1.322ZM506.528 105.576h-29.532c.794 6.876 5.025 10.828 11.99 10.828 6.082 0 9.608-2.38 12.34-4.32 2.205 1.41 3.969 4.232 4.32 6.789-3.525 2.82-8.991 5.906-17.366 5.906-12.518 0-21.51-8.286-21.51-21.951 0-12.96 9.08-22.304 20.98-22.304 11.198 0 19.131 7.67 19.131 18.866 0 2.204-.087 3.894-.353 6.186Zm-9.433-6.612c0-4.937-2.555-10.241-9.344-10.33-6.172 0-9.961 4.6-10.667 10.33h20.011ZM550.946 97.81v25.654c-1.675.353-3.439.441-5.025.441-1.675 0-3.262-.088-5.026-.44V100.19c0-7.67-3.702-10.58-10.137-10.58-3.88 0-7.318 1.147-9.257 2.293v31.56c-1.586.353-3.35.441-5.025.441-1.587 0-3.262-.088-4.937-.44v-36.41c3.703-3.085 11.02-6.612 20.012-6.612 13.488 0 19.395 7.758 19.395 17.368ZM584.766 120.202c-2.556 2.205-6.787 4.497-12.518 4.497-8.992 0-15.604-4.056-15.604-14.106V71.187c1.675-.353 3.35-.44 5.025-.44s3.262.087 4.848.44v11.108h15.605c.352 1.322.44 2.644.44 3.967 0 1.41-.088 2.733-.44 4.143h-15.605v17.896c0 6.171 2.91 7.758 7.053 7.758 2.998 0 5.642-1.587 6.964-2.82 2.117 1.41 3.88 4.23 4.232 6.963Z" />
        </svg>
      )}
    </>
  );
};

export default SimpleLayout;
